import type { Dispatch, SetStateAction } from "react";
import {
  K_EXTRACTANTS,
  MAX_DEPTH_IN,
  P_EXTRACTANTS,
  UNITS_OPTIONS,
  type SampleDefaultsDraft,
} from "../../lib/sampleDefaults";

const CHOICES = [
  { key: "p_extractant", label: "Phosphorus", opts: P_EXTRACTANTS },
  { key: "k_extractant", label: "K / Micros", opts: K_EXTRACTANTS },
  { key: "units", label: "Units", opts: UNITS_OPTIONS },
] as const;

function ChoiceRow({
  label,
  opts,
  selected,
  onPick,
}: {
  label: string;
  opts: readonly { value: string; label: string }[];
  selected: string | null;
  onPick: (value: string, on: boolean) => void;
}) {
  return (
    <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted">
      <span className="w-20 shrink-0">{label}</span>
      <div className="flex flex-wrap gap-1">
        {opts.map((o) => {
          const on = selected === o.value;
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={on}
              onClick={() => onPick(o.value, on)}
              className={`rounded border px-1.5 py-1 text-[11px] ${
                on
                  ? "border-accent bg-accent text-canvas"
                  : "border-line text-muted hover:bg-hover hover:text-fg"
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SampleOptIn({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-1.5 text-[11px] text-fg">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 accent-[var(--accent,#3b82f6)]"
      />
      <span>
        <span className="font-medium">Upload may contain soil samples</span>
        <span className="block text-[10px] text-faint">
          If unchecked, files that look like soil samples will be ignored. Field
          names must match.
        </span>
      </span>
    </label>
  );
}

function DepthField({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted">
      <span className="w-20 shrink-0">Depth (in)</span>
      <input
        type="number"
        inputMode="decimal"
        step="0.25"
        min="0"
        max={MAX_DEPTH_IN}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. 6.5"
        aria-label="Default sampling depth in inches"
        className="w-20 rounded border border-line bg-transparent px-1.5 py-1 text-[11px] outline-none placeholder:text-faint focus:border-accent"
      />
    </label>
  );
}

function PointIdMatch({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <label className="mt-2 flex items-start gap-1.5 text-[11px] text-fg">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 accent-[var(--accent,#3b82f6)]"
      />
      <span>
        <span className="font-medium">
          Match samples with point IDs if no locations are available in the file
        </span>
        <span className="block text-[10px] text-faint">
          Leave off if unsure to avoid mixing up samples. When on, location-less
          results join live by point ID onto this org’s existing sample
          locations — unmatched IDs are skipped. GPS files still place by
          containment.
        </span>
      </span>
    </label>
  );
}

// Asked before the upload. Nothing is pre-selected, so silence cannot adopt a default.
export function BulkSampleDefaults({
  maySamples,
  setMaySamples,
  matchByPointId,
  setMatchByPointId,
  defaults,
  setDefaults,
  hasFiles,
  problem,
}: {
  maySamples: boolean;
  setMaySamples: (on: boolean) => void;
  matchByPointId: boolean;
  setMatchByPointId: (on: boolean) => void;
  defaults: SampleDefaultsDraft;
  setDefaults: Dispatch<SetStateAction<SampleDefaultsDraft>>;
  hasFiles: boolean;
  problem: string | null;
}) {
  return (
    <div className="border-t border-line p-2">
      <SampleOptIn checked={maySamples} onChange={setMaySamples} />
      {maySamples && (
        <>
          <p className="mt-2 text-[11px] font-medium text-fg">
            If soil samples are uploaded without specified
            depth/extractant/units, use:
          </p>
          <DepthField
            value={defaults.depthText}
            onChange={(value) =>
              setDefaults((d) => ({ ...d, depthText: value }))
            }
          />
          {CHOICES.map((row) => (
            <ChoiceRow
              key={row.key}
              label={row.label}
              opts={row.opts}
              selected={defaults[row.key]}
              onPick={(value, on) =>
                setDefaults((d) => ({
                  ...d,
                  [row.key]: on ? null : value,
                }))
              }
            />
          ))}
          <PointIdMatch checked={matchByPointId} onChange={setMatchByPointId} />
          {hasFiles && problem && (
            <p className="mt-1.5 text-[10px] text-amber-500">{problem}</p>
          )}
        </>
      )}
    </div>
  );
}
